import { pageTitle } from '@palaccount/config';
import { Lock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { Amount } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { DocumentToolbar } from '@/components/document-toolbar';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import {
  useAccounts,
  useCashAccountOptions,
  useExpenseAccountOptions,
} from '@/features/accounts/api';
import { useCan } from '@/lib/permissions';
import { localizeServerCause } from '@/lib/server-messages';
import { useBaseCurrency, useCompanyToday } from '@/lib/session';

import {
  useCancelCashTransfer,
  useCashTransfer,
  useCreateCashTransfer,
  useDeleteCashTransfer,
  usePostCashTransfer,
  useUpdateCashTransfer,
} from './api';
import { BankingTabs } from './banking-tabs';
import { transferTotals } from './transfer-math';

/**
 * FIN-06 — Cash Transfer: money between the company's own cash and bank
 * ledger accounts (the postable 1110 children, Q-FIN-06-1's one list). The
 * §5b fee modes drive the two summary tiles live; posting is the strongest
 * lock in the part — every figure freezes and only the notes keep accepting
 * typing (BR-FIN-06-13).
 */
export function TransferEditorPage() {
  // N-16: the COMPANY's today, never the reader's machine clock.
  const today = useCompanyToday();
  // FIN-01 field 6: a chart account with no currency of its own IS in the
  // base, and which currency that is comes from the session (GEN-107). It was
  // a module constant 'USD', so for a euro company every base account read as
  // a dollar one — and two euro accounts were called same-currency by
  // coincidence rather than by the rule.
  const BASE = useBaseCurrency();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { transferId } = useParams<{ transferId: string }>();
  const { data: existing } = useCashTransfer(transferId);
  const { options: cashOptions } = useCashAccountOptions();
  const { options: expenseOptions } = useExpenseAccountOptions();
  const { data: chart } = useAccounts();
  const create = useCreateCashTransfer();
  const update = useUpdateCashTransfer();
  const post = usePostCashTransfer();
  const cancel = useCancelCashTransfer();
  const remove = useDeleteCashTransfer();
  const canEdit = useCan('banking.edit');

  const [form, setForm] = useState({
    transferDate: today,
    reference: '',
    fromAccountId: '',
    toAccountId: '',
    amount: '',
    exchangeRate: '',
    fee: '',
    feeMode: 'deduct' as 'deduct' | 'expense',
    feeExpenseAccountId: '',
    notes: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /**
   * GEN-59 (spec line 3807): the cancel asks through §8's typed-reason
   * `ReasonBox` — "Cancel must always ask for a reason, and must store that
   * reason on the record." The contract carries it (banking.ts cancel body),
   * the service writes `cancel_reason` + the audit-row reason
   * (20260921100000), and the cancelled notice reads it back. The undone
   * clause is in the prompt because cancelled is TERMINAL: the service 409s
   * a second cancel (`transferCancelledLocked`), the lock trigger's terminal
   * branch is notes-only, and no un-cancel route is on the contract (the
   * wires test asserts its absence).
   */
  const [cancelAsk, setCancelAsk] = useState(false);

  useEffect(() => {
    document.title = pageTitle(t('banking.transferTitle'));
  }, [t]);

  useEffect(() => {
    if (!existing || loaded) return;
    setForm({
      transferDate: existing.transferDate,
      reference: existing.reference ?? '',
      fromAccountId: existing.fromAccountId,
      toAccountId: existing.toAccountId,
      amount: existing.amount,
      exchangeRate: existing.rateIsManual ? existing.exchangeRate : '',
      fee: Number(existing.fee) > 0 ? existing.fee : '',
      feeMode: existing.feeMode ?? 'deduct',
      feeExpenseAccountId: existing.feeExpenseAccountId ?? '',
      notes: existing.notes ?? '',
    });
    setLoaded(true);
  }, [existing, loaded]);

  const locked = existing !== undefined && existing.status !== 'draft';
  const sameAccounts =
    form.fromAccountId !== '' && form.fromAccountId === form.toAccountId;

  // BR-FIN-06-04: the cross-currency fields appear only when the two
  // accounts really are in different currencies — a null currency on the
  // chart means the base (FIN-01 field 6).
  const currencyOf = (accountId: string): string => {
    const account = (chart ?? []).find((row) => row.id === accountId);
    return account?.currency ?? BASE;
  };
  const fromCurrency = form.fromAccountId === '' ? BASE : currencyOf(form.fromAccountId);
  const toCurrency = form.toAccountId === '' ? BASE : currencyOf(form.toAccountId);
  const crossCurrency = fromCurrency !== toCurrency;

  // The automatic rate is the FIN-05 table's cross rate at the transfer
  // date, computed by the server (§5b: destination ÷ source, then MULTIPLY).
  // The editor shows what the server settled on, and a typed rate overrides.
  const effectiveRate =
    form.exchangeRate !== ''
      ? form.exchangeRate
      : (existing?.exchangeRate ?? '1');

  const totals = useMemo(
    () =>
      transferTotals({
        amount: form.amount,
        fee: form.fee,
        feeMode: Number(form.fee) > 0 ? form.feeMode : null,
        rate: effectiveRate,
        crossCurrency,
      }),
    [form.amount, form.fee, form.feeMode, effectiveRate, crossCurrency],
  );

  /**
   * Second route, same as the other two banking screens: this never called
   * `localizeServerMessage`, so a refusal that arrived was shown in the
   * server's English and a request that never came back showed the browser's
   * `Failed to fetch`. Both faults come from discarding the cause, so one call
   * answers both.
   */
  const fail = (cause: unknown) =>
    setError(localizeServerCause(t, cause, 'banking.errSaveFailedTransfer'));

  const buildBody = () => ({
    transferDate: form.transferDate,
    ...(form.reference.trim() === '' ? {} : { reference: form.reference.trim() }),
    fromAccountId: form.fromAccountId,
    toAccountId: form.toAccountId,
    ...(form.amount === '' ? {} : { amount: form.amount }),
    // Only a TYPED rate travels: an untouched box leaves the server on the
    // FIN-05 table's automatic cross rate (BR-FIN-06-07).
    ...(crossCurrency && form.exchangeRate.trim() !== ''
      ? { exchangeRate: form.exchangeRate.trim() }
      : {}),
    ...(form.fee === '' ? {} : { fee: form.fee }),
    ...(Number(form.fee) > 0 ? { feeMode: form.feeMode } : {}),
    ...(form.feeExpenseAccountId === '' ? {} : { feeExpenseAccountId: form.feeExpenseAccountId }),
    ...(form.notes === '' ? {} : { notes: form.notes }),
  });

  /**
   * BR-FIN-06-06's reset, told to the server. An emptied box on a transfer
   * whose STORED rate is manual travels as an explicit `exchangeRate: null`,
   * which the service answers by recomputing the FIN-05 automatic rate and
   * dropping `rate_is_manual`. Update-only (a new transfer has no stored
   * manual rate to reset — its empty box is already automatic), and merely
   * omitting the field here would leave the stored rate manual for ever,
   * which made the reset link a lie.
   *
   * GEN-17b — "A system-supplied default must be a safe, reversible choice
   * that the user can change on the same record afterwards. The system must
   * never default a value that cannot be corrected once written." — is what
   * this line pays, on this screen's system-supplied default: the automatic
   * FIN-05 rate. Safe (the table's own figure at the transfer date), and
   * changeable on the same record afterwards in BOTH directions — typing
   * overrides it (BR-FIN-06-06), and this explicit null is the way BACK,
   * without which the override could never be corrected once written.
   * `fin06-transfer-rules.test.tsx` proves the way back is real on the wire
   * ("Reset tells the server"). The rule is universal over defaults; this is
   * its enforcement on this screen's one stored default, not a claim about
   * every default in the product.
   */
  const updateBody = () => ({
    ...buildBody(),
    ...(existing?.rateIsManual && !(crossCurrency && form.exchangeRate.trim() !== '')
      ? { exchangeRate: null }
      : {}),
  });

  const save = async (): Promise<string | null> => {
    setError(null);
    try {
      if (existing) {
        await update.mutateAsync({ transferId: existing.id, body: updateBody() });
        return existing.id;
      }
      const created = await create.mutateAsync(buildBody());
      void navigate(`/banks/transfers/${created.id}`, { replace: true });
      return created.id;
    } catch (cause) {
      fail(cause);
      return null;
    }
  };

  const doPost = async () => {
    const id = existing?.id ?? (await save());
    if (id === null || id === undefined) return;
    if (existing) {
      // Persist pending edits before posting.
      try {
        await update.mutateAsync({ transferId: id, body: updateBody() });
      } catch (cause) {
        fail(cause);
        return;
      }
    }
    post.mutateAsync(id).catch(fail);
  };

  const field = (key: keyof typeof form, value: string) =>
    setForm((state) => ({ ...state, [key]: value }));

  const feeActive = Number(form.fee) > 0;

  return (
    <div className="grid gap-4">
      <BankingTabs />
      {/* GEN-36 — the sixteenth site, pinned exactly like the other fifteen.
          The status pill below is a bare <span>, NOT the shared StatusChip;
          swapping it would change how this screen looks and is the owner's
          call, so only the pin is applied here. */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="me-auto text-lg font-bold">
          {t('banking.transferTitle')}
          {existing ? (
            <span className="ms-2 font-mono text-sm text-muted-foreground">{existing.code}</span>
          ) : null}
        </h2>
        {existing ? (
          <span className="rounded-full bg-muted px-3 py-1 text-sm font-medium" data-testid="transfer-status">
            {t(`common.status.${existing.status}`)}
          </span>
        ) : null}
        {/* THE ACTS, DECLARED — delegated ruling D51 (27 Aug 2026): this
            screen "gets the standard sticky toolbar in the place the other
            fourteen put it". A layout ADDITION, authorised by the ruling: the
            acts leave the row at the foot of the form and join the sticky
            header; every guard, gate, wire and label is byte-equal. Two
            departures from D51's own description, both because the ruling
            tracked a roster note the screen had outgrown:

            - NO BACK LINK MOVED, because none exists. The fourteen's back
              link wears `common.cancel`, and THIS screen already prints that
              word on its cancel ACT below — adding a second "Cancel" that
              navigates would mint the one-label-two-meanings collision the
              primitive's header records GEN-55 forbidding, and a new label
              would be a new string this change is not licensed to add.
              Recorded, not built.
            - POST STAYED AT THE FOOT: FIN-06 §6 pins it — "Post transfer |
              Posting panel" — and the other fourteen keep Post out of the
              toolbar for the same reason. */}
        <DocumentToolbar
          actions={[
            {
              // GEN-58's second clause lives in `visible:` — `!locked` is
              // `status !== 'draft'` read the other way, so a posted or
              // cancelled transfer has no Delete for anybody (GEN-70,
              // BR-PERM-02), while a reader on a draft sees it greyed with
              // the reason (16.7 row 2).
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              variant: 'destructive',
              visible: existing !== undefined && !locked,
              gate: { permission: 'banking.delete', reasonKey: 'perm.cannot.deleteCashTransfers' },
              onAct: () => setDeleting(true),
            },
            {
              // PART 4.6's Cancel row by the word printed on it — R8's
              // Draft → Cancelled arm. Of the TWO deviations D51's byte-equal
              // move recorded here, one remains and one was paid: it still
              // acts on a DRAFT (R8 replaced the spec's Status control with
              // this lifecycle), and it ASKED FOR NO REASON until the GEN-59
              // wave (20260921100000) built the ask the record had queued —
              // GEN-208: the click now opens the question and does nothing
              // else; the write happens on the dialog's own verb, with the
              // typed reason, never here.
              id: 'cancel',
              row: 'cancel',
              label: t('common.cancel'),
              variant: 'outline',
              visible: existing !== undefined && !locked,
              gate: { permission: 'banking.edit', reasonKey: 'perm.cannot.cancelCashTransfers' },
              onAct: () => setCancelAsk(true),
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              variant: 'outline',
              visible: !locked,
              gate: { permission: 'banking.edit', reasonKey: 'perm.cannot.editCashTransfers' },
              // This Save never had a spinner, and GEN-60 puts the spinner
              // and the disabling together or not at all — so `disabled`,
              // not `busy` (the migrated fourteen's convention).
              disabled:
                create.isPending ||
                update.isPending ||
                form.fromAccountId === '' ||
                form.toAccountId === '',
              onAct: () => void save(),
            },
            {
              // Notes stay editable after posting (BR-FIN-06-13's carve-out:
              // commentary, not a figure), so the locked document keeps
              // exactly one act. Not a PART 4.6 row — "Save notes" is not a
              // word in the table.
              id: 'saveNotes',
              row: null,
              label: t('banking.saveNotes'),
              variant: 'outline',
              visible: locked,
              gate: { permission: 'banking.edit', reasonKey: 'perm.cannot.editNotes' },
              onAct: () => {
                if (!existing) return;
                setError(null);
                update
                  .mutateAsync({
                    transferId: existing.id,
                    body: { notes: form.notes === '' ? null : form.notes },
                  })
                  .catch(fail);
              },
            },
          ]}
        >
          {/* THIS DELETED A CASH TRANSFER WITH NO QUESTION AT ALL and
              navigated away. GEN-58: "Delete must always ask." It had no
              `*.confirmDelete` key, which is exactly why the key-based
              census could not see it — a delete that asks nothing has
              nothing to enumerate. `!locked` is `status !== 'draft'`, so
              GEN-58's second clause was already satisfied here; only the
              asking was missing. The dialog keeps its own ternary — the
              fourteen-screen convention — so it cannot outlive the state
              that allows the delete. */}
          {existing && !locked ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('banking.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => {
                setDeleting(false);
                setError(null);
                remove
                  .mutateAsync(existing.id)
                  .then(() => void navigate('/banks/transfers'))
                  .catch(fail);
              }}
              busy={remove.isPending}
            />
          ) : null}
          {/* GEN-59's ask, in the same `existing && !locked` guard as the
              Cancel act's `visible:` — the fourteen-screen convention: the
              dialog cannot outlive the state that allows the cancel. The
              `ReasonBox` IS the confirm (the journal/voucher shape): the
              reason is required, so Escape, a click outside or an empty box
              abandons the act and sends nothing. `gen72.cancelReasonLabel` /
              `gen72.confirmCancel` are the shared GEN-59 pair — the verb is
              deliberately not the trigger's exact word, which here is the
              bare 'Cancel' (reason-box.tsx's own rule). */}
          {existing && !locked ? (
            <ReasonBox
              open={cancelAsk}
              title={t('banking.confirmCancelTitle')}
              prompt={t('banking.confirmCancelBody')}
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={cancel.isPending}
              onConfirm={(reason) => {
                setCancelAsk(false);
                setError(null);
                cancel.mutateAsync({ transferId: existing.id, reason }).catch(fail);
              }}
              onCancel={() => setCancelAsk(false)}
            />
          ) : null}
        </DocumentToolbar>
      </div>

      {locked ? (
        <p className="flex items-center gap-2 rounded-md border border-green-300 bg-green-50 p-3 text-sm dark:border-green-900 dark:bg-green-950">
          <Lock aria-hidden className="size-4" />
          {existing?.status === 'posted'
            ? t('banking.transferPostedNotice')
            : existing?.cancelReason != null
              ? // GEN-59's stored reason, read back OFF THE RECORD. Rows
                // cancelled before 20260921100000 have none and keep the
                // plain notice — NULL is the true answer there.
                t('banking.transferCancelledReasonNotice', { reason: existing.cancelReason })
              : t('banking.transferCancelledNotice')}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <Card>
        <CardContent className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="t-date">{t('common.date')}</Label>
              <Input
                id="t-date"
                type="date"
                disabled={locked || !canEdit}
                value={form.transferDate}
                onChange={(event) => field('transferDate', event.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="t-ref">{t('banking.fReference')}</Label>
              <Input
                id="t-ref"
                placeholder={t('banking.referencePlaceholder')}
                disabled={locked || !canEdit}
                value={form.reference}
                onChange={(event) => field('reference', event.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="t-from">{t('banking.fFromAccount')}</Label>
              <NativeSelect
                id="t-from"
                disabled={locked || !canEdit}
                value={form.fromAccountId}
                onChange={(event) => field('fromAccountId', event.target.value)}
              >
                <option value="">{t('common.choose')}</option>
                {cashOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.code} — {option.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="t-to">{t('banking.fToAccount')}</Label>
              <NativeSelect
                id="t-to"
                disabled={locked || !canEdit}
                value={form.toAccountId}
                onChange={(event) => field('toAccountId', event.target.value)}
              >
                <option value="">{t('common.choose')}</option>
                {cashOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.code} — {option.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>
          {sameAccounts ? (
            <p role="alert" className="text-sm text-destructive">
              {t('banking.sameAccountsWarn')}
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label htmlFor="t-amount">{t('banking.fTransferAmount')}</Label>
              <Input
                id="t-amount"
                inputMode="decimal"
                dir="ltr"
                disabled={locked || !canEdit}
                value={form.amount}
                onChange={(event) => field('amount', event.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="t-fee">
                {t('banking.fTransferFee')}{' '}
                <span className="text-xs text-muted-foreground">· {t('banking.optionalTag')}</span>
              </Label>
              <Input
                id="t-fee"
                inputMode="decimal"
                dir="ltr"
                disabled={locked || !canEdit}
                value={form.fee}
                onChange={(event) => field('fee', event.target.value)}
              />
            </div>
            {/* BR-FIN-06-04 / FIN-05 §5b: the rate and its converted figure,
                shown only across currencies. Left blank, the server reads
                the FIN-05 table at the transfer date; typed, it overrides
                and the row is flagged manual. */}
            {crossCurrency ? (
              <div className="grid gap-1.5">
                {/*
                 * BR-FIN-06-06 — "Entering anything in the rate box must
                 * switch it to manual, show a manual tag, and offer a reset."
                 *
                 * The tag reads the box, and the box carries the STORED fact:
                 * the load effect fills it from `existing.exchangeRate` only
                 * when `rateIsManual` is set, so a saved manual rate wears the
                 * tag on a fresh load and a saved automatic one does not — the
                 * tag never depends on what this session happened to type.
                 * "Anything" is literal (ERR-FIN-06-05 leans on it): rubbish
                 * switches to manual too, which is what makes the reset the
                 * row's remedy rather than a convenience.
                 */}
                <div className="flex items-center gap-2">
                  <Label htmlFor="t-rate">{t('banking.fExchangeRate')}</Label>
                  {form.exchangeRate.trim() !== '' ? (
                    <>
                      <span className="text-xs text-muted-foreground">
                        {t('banking.rateManualTag')}
                      </span>
                      <button
                        type="button"
                        className="text-xs text-primary underline-offset-2 hover:underline disabled:opacity-50"
                        disabled={locked || !canEdit}
                        onClick={() => field('exchangeRate', '')}
                      >
                        {t('banking.rateReset')}
                      </button>
                    </>
                  ) : null}
                </div>
                <Input
                  id="t-rate"
                  inputMode="decimal"
                  dir="ltr"
                  placeholder={existing?.exchangeRate ?? ''}
                  disabled={locked || !canEdit}
                  value={form.exchangeRate}
                  onChange={(event) => field('exchangeRate', event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {existing !== undefined && !existing.rateIsManual
                    ? t('banking.rateFromTable', {
                        rate: existing.exchangeRate,
                        from: fromCurrency,
                        to: toCurrency,
                      })
                    : t('banking.rateOverrideHint')}
                </p>
              </div>
            ) : null}
          </div>

          {crossCurrency ? (
            <p className="text-sm">
              {t('banking.convertedAmount')}{' '}
              <Amount value={totals.convertedAmount ?? '0.00'} currency={toCurrency} />
            </p>
          ) : null}

          {/* BR-FIN-06-08: the mode selector appears only above zero. */}
          {feeActive ? (
            <div className="grid gap-3">
              <div className="flex gap-2">
                {(['deduct', 'expense'] as const).map((mode) => (
                  <Gated
                    key={mode}
                    permission="banking.edit"
                    reasonKey="perm.cannot.editCashTransfers"
                  >
                    <Button
                      type="button"
                      variant={form.feeMode === mode ? 'default' : 'outline'}
                      size="sm"
                      disabled={locked}
                      onClick={() => field('feeMode', mode)}
                    >
                      {mode === 'deduct' ? t('banking.feeDeduct') : t('banking.feeExpense')}
                    </Button>
                  </Gated>
                ))}
              </div>
              {form.feeMode === 'expense' ? (
                <div className="grid gap-1.5 sm:max-w-sm">
                  <Label htmlFor="t-fee-account">{t('banking.fFeeExpenseAccount')}</Label>
                  <NativeSelect
                    id="t-fee-account"
                    disabled={locked || !canEdit}
                    value={form.feeExpenseAccountId}
                    onChange={(event) => field('feeExpenseAccountId', event.target.value)}
                  >
                    <option value="">{t('common.choose')}</option>
                    {expenseOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.code} — {option.name}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* The two §5b tiles, currency codes printed (BR-FIN-06-15). */}
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">{t('banking.totalFromSource')}</p>
              <p className="font-bold">
                <Amount
                  value={existing && locked ? existing.totalFromSource : totals.totalFromSource}
                  currency={existing?.fromCurrency ?? BASE}
                />
              </p>
            </div>
            <div className="rounded-lg border border-green-300 p-3 dark:border-green-900">
              <p className="text-xs text-muted-foreground">{t('banking.netToDestination')}</p>
              <p className="font-bold text-green-700 dark:text-green-400">
                <Amount
                  value={existing && locked ? existing.netToDestination : totals.netToDestination}
                  currency={existing?.toCurrency ?? BASE}
                />
              </p>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="t-notes">{t('common.notes')}</Label>
            {/* Notes stay editable after posting — commentary, not a figure. */}
            <Textarea
              id="t-notes"
              rows={2}
              disabled={!canEdit}
              placeholder={t('banking.transferNotesPlaceholder')}
              value={form.notes}
              onChange={(event) => field('notes', event.target.value)}
            />
          </div>

          {/* The posting panel's one remaining button — FIN-06 §6 pins Post
              here ("Post transfer | Posting panel"); D51 moved Save, Cancel,
              Delete and Save notes into the sticky toolbar above. */}
          <div className="flex flex-wrap gap-2">
            {!locked ? (
              <Gated permission="banking.approve" reasonKey="perm.cannot.postCashTransfers">
              <Button
                data-testid="post-transfer"
                onClick={() => void doPost()}
                // ERR-FIN-06-01/-02: greyed on same accounts or no amount.
                disabled={
                  sameAccounts ||
                  form.amount === '' ||
                  Number(form.amount) <= 0 ||
                  form.fromAccountId === '' ||
                  form.toAccountId === '' ||
                  post.isPending
                }
              >
                {t('banking.postTransfer')}
              </Button>
              </Gated>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
